import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useExpertStore from '../../store/useExpertStore';

const CheckIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>;
const SparklesIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.712L18 9.75l-.259-1.038a3.375 3.375 0 00-2.453-2.453L14.25 6l1.038-.259a3.375 3.375 0 002.453-2.453L18 2.25l.259 1.038a3.375 3.375 0 002.453 2.453L21.75 6l-1.038.259a3.375 3.375 0 00-2.453 2.453zM16.75 16.5l.428 1.286a1.125 1.125 0 001.386.682l1.286-.428-1.286-.428a1.125 1.125 0 00-.682-1.386L16.75 16.5z" /></svg>;
const BotIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 3v1.5M4.5 8.25H3m18 0h-1.5M4.5 12H3m18 0h-1.5m-15 3.75H3m18 0h-1.5M8.25 19.5V21M12 3v1.5m0 15V21m3.75-18v1.5m0 15V21m-9-1.5h10.5a2.25 2.25 0 002.25-2.25V6.75a2.25 2.25 0 00-2.25-2.25H6.75A2.25 2.25 0 004.5 6.75v10.5a2.25 2.25 0 002.25 2.25zm.75-12h9v9h-9v-9z" /></svg>;

const ExpertSelector = ({ selectedExpertId, onSelect, className = "" }) => {
    const { experts, fetchExperts, loading } = useExpertStore();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        fetchExperts();
    }, [fetchExperts]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedExpert = experts.find(e => e.id === selectedExpertId);

    const isImageless = selectedExpert && !selectedExpert.imageUrl;

    return (
        <div className={`relative ${className}`} ref={containerRef}>
            <motion.button
                type="button"
                whileHover={{ backgroundColor: isImageless ? undefined : 'rgba(155, 155, 155, 0.1)', scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.1 }}
                onClick={() => setIsOpen(!isOpen)}
                title={selectedExpert?.name || "Select Agent"}
                style={{
                    backgroundColor: isImageless ? 'var(--text-main)' : (isOpen ? 'rgba(99, 102, 241, 0.1)' : undefined),
                    color: isImageless ? 'var(--bg-main)' : undefined
                }}
                className={`flex items-center justify-center w-8 h-8 rounded-full border transition-all overflow-hidden ${isOpen
                    ? 'border-indigo-500/50 shadow-[0_0_10px_rgba(99,102,241,0.2)]'
                    : 'border-[var(--border-subtle)] hover:border-[var(--border-strong)]'
                    }`}
            >
                {selectedExpert?.imageUrl ? (
                    <img src={selectedExpert.imageUrl} alt="" className="w-full h-full object-cover rounded-full" />
                ) : selectedExpert ? (
                    <BotIcon className="w-5 h-5" />
                ) : (
                    <SparklesIcon className="w-4 h-4 text-[var(--text-muted)]" />
                )}
            </motion.button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 10 }}
                        transition={{ type: "spring", damping: 20, stiffness: 300 }}
                        className="absolute bottom-full mb-3 left-0 w-64 bg-[var(--bg-secondary)]/95 backdrop-blur-xl border border-[var(--border-accent)] rounded-2xl shadow-2xl overflow-hidden z-[999] p-1.5"
                    >
                        <div className="px-3 py-2 border-b border-[var(--border-subtle)] mb-1 flex items-center justify-between">
                            <span className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-widest text-opacity-70">Switch Agent</span>
                            {selectedExpertId && (
                                <button
                                    onClick={() => { onSelect(null); setIsOpen(false); }}
                                    className="text-[10px] font-medium text-indigo-400 hover:text-indigo-300 transition-colors"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                        <div className="max-h-60 overflow-y-auto custom-scrollbar p-0.5">
                            {/* Expert Personas */}
                            {experts.map((expert) => {
                                const isSelected = expert.id === selectedExpertId;
                                return (
                                    <motion.button
                                        key={expert.id}
                                        type="button"
                                        whileHover={{ backgroundColor: 'rgba(155, 155, 155, 0.1)' }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={() => {
                                            onSelect(expert.id);
                                            setIsOpen(false);
                                        }}
                                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-all mb-0.5 ${isSelected ? 'bg-indigo-500/10 border border-indigo-500/20' : 'border border-transparent'}`}
                                    >
                                        <div
                                            style={{
                                                backgroundColor: expert.imageUrl ? undefined : 'var(--text-main)',
                                                color: expert.imageUrl ? undefined : 'var(--bg-main)'
                                            }}
                                            className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 overflow-hidden shadow-sm"
                                        >
                                            {expert.imageUrl ? (
                                                <img src={expert.imageUrl} alt="" className="w-full h-full object-cover" />
                                            ) : (
                                                <BotIcon className="w-5 h-5" />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[13px] font-semibold text-[var(--text-main)] truncate">{expert.name}</div>
                                            <div className="text-[10px] text-[var(--text-muted)] truncate">{expert.role || 'Custom Agent'}</div>
                                        </div>
                                        {isSelected && <CheckIcon className="w-4 h-4 text-indigo-400" />}
                                    </motion.button>
                                );
                            })}

                            {experts.length === 0 && !loading && (
                                <div className="p-4 text-center">
                                    <div className="text-xs text-[var(--text-muted)] mb-2">No custom agents yet.</div>
                                    <div className="text-[10px] text-[var(--text-muted)] opacity-60">Create one in the Expert Forge.</div>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ExpertSelector;
